import { effect, inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { EnteAutocompleteItem } from 'cdc-conte-ui-kit';
import {
  extractTipoEnteIds,
  RicercaDocumentiParams,
  TipoEnteControlValue,
  toRicercaDateParam,
} from '../models/interfaces/documenti-adempimenti/filtri-ricerca-gestione-documenti.interface';
import { FiltriDocumentiAdempimentiStore } from '../store/filtri-documenti-adempimenti.store';
import { FormGroup } from '@angular/forms';
import { SelectOptionsInterface } from '../models/interfaces/documenti-adempimenti/carica-documento.interfaces';
import { RouterServiceConte } from './router-service';
import {
  toRichiestaInvioParam,
  toRichiestaInvioSelectId,
} from '../features/documenti-adempimenti/gestione-documenti/filtri-ricerca-gestione-documenti/richiesta-invio.util';

@Injectable({
  providedIn: 'root',
})
export class FiltriRicercaGestioneDocumentiService {
   reloadDocuments$ = new Subject<void>();
   indietroBtnHandler$ = new BehaviorSubject<null | boolean>(null);
   storeFiltriGestioneDocumenti = inject(FiltriDocumentiAdempimentiStore);
   routerServiceConte = inject(RouterServiceConte);
   storeFiltriDocumenti = inject(FiltriDocumentiAdempimentiStore);

  extractSelectId(value: SelectOptionsInterface | number | null | undefined): number | undefined {
    if (value == null) {
      return undefined;
    }
    return typeof value === 'number' ? value : value.id;
  }

   buildRicercaParams(form: FormGroup, formFilters: FormGroup, adempimentoNormativoVal: boolean, idToAssignToFase: number, is_GAR_ASR_CFR_FEV: boolean, isTrentoOrBolzano:boolean): RicercaDocumentiParams {
    const params: RicercaDocumentiParams = {};

    const rawText = form.get('search')?.value;
    const text = typeof rawText === 'string' && rawText.length > 1 ? rawText.trim() : '';
    if (text) {
      params.text = text;
    }

    const esercizio = formFilters.get('esercizio')?.value;
    const sequEsercizio = this.extractSelectId(esercizio);
    if (sequEsercizio != null) {
      params.sequEsercizio = sequEsercizio;
    }

    const fase = formFilters.get('fase')?.value;
    const sequFase = adempimentoNormativoVal && idToAssignToFase
      ? idToAssignToFase
      : this.extractSelectId(fase);
    if (sequFase != null) {
      params.sequFase = sequFase;
    }

    const listaTipiEnte = extractTipoEnteIds(formFilters.get('tipoEnte')?.value as TipoEnteControlValue);
    if (listaTipiEnte.length) {
      params.listaTipiEnte = listaTipiEnte;
    }

    const sequRegione =  this.extractSelectId(formFilters.get('regione')?.value as SelectOptionsInterface);
    
    if(is_GAR_ASR_CFR_FEV){
      params.sequRegione = undefined 
    } else if (sequRegione != null) {
       params.sequRegione = sequRegione 
    }

    const sequProvincia = this.extractSelectId(formFilters.get('provinciaCittaMetropolitana')?.value as SelectOptionsInterface);
    if(is_GAR_ASR_CFR_FEV && isTrentoOrBolzano){
      params.sequRegione = undefined 
    } else if (sequProvincia != null) {
      params.sequProvincia = sequProvincia;
    }

    const denominazioneEnte = formFilters.get('denominazioneEnte')?.value;
    if (denominazioneEnte && typeof denominazioneEnte === 'object' && denominazioneEnte.idEnte != null) {
      params.sequEnte = String(denominazioneEnte.idEnte);
      // Salviamo anche l'oggetto completo (con la descrizione), non ricostruibile dal solo id,
      // per poter ripristinare visivamente il campo 'Denominazione ente' al ritorno sulla pagina.
      this.storeFiltriGestioneDocumenti.saveDenominazioneEnteSelezionata(denominazioneEnte as EnteAutocompleteItem);
    } else {
      this.storeFiltriGestioneDocumenti.saveDenominazioneEnteSelezionata(null);
    }

    const sequAdempimento = this.extractSelectId(formFilters.get('adempimentoNormativo')?.value);
    if (sequAdempimento != null) {
      params.sequAdempimento = sequAdempimento;
    }

    const sequTipoDocumento = this.extractSelectId(formFilters.get('tipoDocumento')?.value);
    if (sequTipoDocumento != null) {
      params.sequTipoDocumento = sequTipoDocumento;
    }

    const sequStato = this.extractSelectId(formFilters.get('statoDocumento')?.value);
    if (sequStato != null) {
      params.sequStato = sequStato;
    }

    const sequFonte = this.extractSelectId(formFilters.get('sistemaDiProvenienza')?.value);
    if (sequFonte != null) {
      params.sequFonte = sequFonte;
    }

    const dataInvioDa = formFilters.get('dataInvioDa')?.value;
    if (dataInvioDa) {
      params.dataInvioMin = toRicercaDateParam(dataInvioDa);
    }

    const dataInvioA = formFilters.get('dataInvioA')?.value;
    if (dataInvioA) {
      params.dataInvioMax = toRicercaDateParam(dataInvioA);
    }

    const richiestaInvio = toRichiestaInvioParam(
      this.extractSelectId(formFilters.get('richiestaInvio')?.value),
    );
    if (richiestaInvio !== undefined) {
      params.richiestaInvio = richiestaInvio;
    }

    if(params){
      this.buildStoreFilter(params);
    }

    return params;
  }

  /**
   * Ripristina nei form i valori precedentemente salvati nello store (operazione inversa di
   * `buildRicercaParams`), così da mostrare all'utente gli stessi filtri usati nell'ultima ricerca
   * (es. al ritorno dalla pagina di dettaglio documento tramite il tasto 'Indietro').
   * @important la denominazione ente NON viene ripristinata da questo metodo: lo store conserva qui
   * solo l'id (sequEnte), non l'oggetto completo (con relativa descrizione) mostrato nell'autocomplete.
   * Il ripristino visivo di quel campo viene gestito separatamente dal chiamante, a partire
   * dall'oggetto completo salvato in `denominazioneEnteSelezionata` nello store.
   */
  applyParamsToForm(form: FormGroup, formFilters: FormGroup, params: RicercaDocumentiParams): void {
    form.get('search')?.setValue(params.text ?? '', { emitEvent: false });

    const patchIfPresent = (controlName: string, value: unknown): void => {
      if (value === null || value === undefined) {
        return;
      }
      formFilters.get(controlName)?.setValue(value, { emitEvent: false });
    };

    patchIfPresent('esercizio', params.sequEsercizio);
    patchIfPresent('fase', params.sequFase);
    // Lo store conserva i soli id dei tipi ente: il multiselect li accetta e risolve da sé
    // l'etichetta dal proprio dataset. La lettura è convertita da `extractTipoEnteIds`, che
    // gestisce sia gli id grezzi ripristinati qui sia gli oggetti emessi dal widget.
    if (params.listaTipiEnte?.length) {
      patchIfPresent('tipoEnte', [...params.listaTipiEnte]);
    }
    patchIfPresent('regione', params.sequRegione);
    patchIfPresent('provinciaCittaMetropolitana', params.sequProvincia);
    patchIfPresent('adempimentoNormativo', params.sequAdempimento);
    patchIfPresent('tipoDocumento', params.sequTipoDocumento);
    patchIfPresent('statoDocumento', params.sequStato);
    patchIfPresent('sistemaDiProvenienza', params.sequFonte);
    patchIfPresent('dataInvioDa', params.dataInvioMin);
    patchIfPresent('dataInvioA', params.dataInvioMax);
    patchIfPresent('richiestaInvio', toRichiestaInvioSelectId(params.richiestaInvio));
  }

  hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return !isNaN(value);
  return true;
}

 hasAtLeastOneFilter(filtriStoreObj: RicercaDocumentiParams): boolean {
  return Object.values(filtriStoreObj).some(this.hasValue);
}

  buildStoreFilter(oggettoFiltri:RicercaDocumentiParams){
    let filtriStoreObj: RicercaDocumentiParams;
    if(oggettoFiltri){

       filtriStoreObj = {
        text: oggettoFiltri?.text ,
        sequEsercizio: oggettoFiltri?.sequEsercizio ,
        sequFase: oggettoFiltri?.sequFase ,
        listaTipiEnte: oggettoFiltri?.listaTipiEnte ,
        sequRegione: oggettoFiltri?.sequRegione ,
        sequProvincia: oggettoFiltri?.sequProvincia ,
        sequEnte: oggettoFiltri?.sequEnte ,
        sequAdempimento: oggettoFiltri?.sequAdempimento ,
        sequTipoDocumento: oggettoFiltri?.sequTipoDocumento ,
        sequStato: oggettoFiltri?.sequStato ,
        sequFonte: oggettoFiltri?.sequFonte ,
        dataInvioMin: oggettoFiltri?.dataInvioMin ,
        dataInvioMax: oggettoFiltri?.dataInvioMax ,
        richiestaInvio: oggettoFiltri?.richiestaInvio ,
      }

      const hasAnyValue = this.hasAtLeastOneFilter(filtriStoreObj);



      if(hasAnyValue){
        this.storeFiltriGestioneDocumenti.saveFiltriRicerca(filtriStoreObj);
        const storati = this.storeFiltriGestioneDocumenti.filtriSalvati();
      } else {
        this.storeFiltriGestioneDocumenti.resetState();
      }
    }
  }

  /**
   *  @desc metodo per resettare i filtri di ricerca leggendo la route (uscendo da url 'gestione', lo store dei filtri di ricerca viene resettato ) 
   */
  handlerFiltriCachati() {
    effect(() => {
      const resettaFiltri = this.routerServiceConte.resetFiltriCachati();
      if (resettaFiltri) {
        this.storeFiltriDocumenti.resetState();
        this.reloadDocuments$.next();
      }
    });

  }
 
}
